import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query"; 
import {
  BookOpen,
  Music,
  Book,
  Lock,
  Feather,
  Sparkles,
  Mic,
  Headphones,
  BookMarked,
  FileText,
  Film,
  MessageCircle,
} from "lucide-react";
import { useProfileByUsername } from "../hooks/profileQueries";
import { useUserPostsByType } from "../hooks/postQueries";
import Sidebar from "../utils/unifiedSidebar";
import HeaderSection from "../components/Profile/HeaderSection";
import ContentTabsSection from "../components/Profile/ContentTabsSection";
import PostsGridSection from "../components/Profile/PostsGridSection";
import BottomNavbar from "../utils/unifiedBottom";
import ProfileSkeleton from "../components/Loaders/ProfileSkeleton";
import PostsGridSkeleton from "../components/Loaders/PostsGridSkeleton";
import { backgroundOptions } from "../utils/postConstants";
import NotFound from "./NotFound";

const allCategories = [
  { type: "Poetry", icon: Feather },
  { type: "Shayari", icon: Sparkles },
  { type: "Ghazal", icon: BookOpen },
  { type: "Song", icon: Music },
  { type: "Rap", icon: Mic },
  { type: "Hip-Hop", icon: Headphones },
  { type: "Novel", icon: Book },
  { type: "Story", icon: BookMarked }, 
  { type: "Book", icon: FileText },
  { type: "Script", icon: Film }, 
  { type: "Quote", icon: MessageCircle },
];

const getBackgroundClass = (backgroundKey) => {
  const bgOption = backgroundOptions[backgroundKey];
  return bgOption ? bgOption.class : backgroundOptions.white.class;
};

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    data: userData,
    isLoading: isLoadingProfile,
    error,
  } = useProfileByUsername(username?.replace("@", ""));

  const [canViewPosts, setCanViewPosts] = useState(
    userData?.isOwnProfile || userData?.privacySettings?.isPrivateAccount === 0
  );

  useEffect(() => {
    const isPublic = userData?.privacySettings?.isPrivateAccount === 0;
    const isFollowingAndAccepted =
      userData?.followStatus?.isFollowing &&
      userData?.followStatus?.isAccepted === 1;
    const canView =
      userData?.isOwnProfile || isPublic || isFollowingAndAccepted;
    setCanViewPosts(canView);
  }, [
    userData?.isOwnProfile,
    userData?.privacySettings?.isPrivateAccount,
    userData?.followStatus?.isFollowing,
    userData?.followStatus?.isAccepted,
  ]);

  const profileID = userData?.profileID;

  // const [activeTab, setActiveTab] = useState("all");
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get("tab") || "all";
  const [activeTab, setActiveTab] = useState(urlTab);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);

    const newSearchParams = new URLSearchParams(location.search);
    if (newTab !== "all") {
      newSearchParams.set("tab", newTab);
    } else {
      newSearchParams.delete("tab");
    }

    const newSearch = newSearchParams.toString();
    const newPath = newSearch
      ? `${location.pathname}?${newSearch}`
      : location.pathname;
    navigate(newPath, { replace: true });
  };

  useEffect(() => {
    const currentUrlTab =
      new URLSearchParams(location.search).get("tab") || "all";
    if (currentUrlTab !== activeTab) {
      setActiveTab(currentUrlTab);
    }
  }, [location.search]);

  const shouldFetchPosts = canViewPosts && profileID && !userData?.isBlocked;

  const {
    data: postsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPosts,
  } = useUserPostsByType(
    shouldFetchPosts ? profileID : null,
    shouldFetchPosts ? activeTab : null,
    10,
    null
  );

  const observerTarget = useRef(null);

  const [viewMode, setViewMode] = useState("grid");
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoadingProfile) {
    return <ProfileSkeleton />;
  }

  // Error state
  if (error) {
    return <NotFound />;
  }

  const typeIcons = allCategories.reduce((acc, category) => {
    acc[category.type] = category.icon;
    return acc;
  }, {});

  const PrivacyLock = ({ name }) => {
    return (
      <div className="min-h-[250px] flex items-center justify-center px-8">
        <div className="flex flex-col items-center gap-1 max-w-md text-center">
          {/* Centered Icon */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-black flex items-center justify-center">
            <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-black stroke-[1.5]" />
          </div>

          {/* Text */}
          <h2 className="text-xl sm:text-2xl font-bold text-black">
            This Account is Private
          </h2>

          <p className="text-black text-sm sm:text-base leading-relaxed">
            Only approved followers can see {name ? `${name}'s` : "this user's"} posts and stories.
          </p>
        </div>
      </div>
    );
  };

  const BlockedState = ({ blockedByMe }) => {
    return (
      <div className="min-h-[250px] flex items-center justify-center px-8">
        <div className="flex flex-col items-center gap-1 max-w-md text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-black flex items-center justify-center">
            <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-black stroke-[1.5]" />
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-black">
            {blockedByMe ? "You've Blocked This Account" : "Profile Unavailable"}
          </h2>

          <p className="text-black text-sm sm:text-base leading-relaxed">
            {blockedByMe
              ? "You won't see posts or activity from this account. Unblock them from Settings to view their profile again."
              : "This profile isn't available right now."}
          </p>
        </div>
      </div>
    );
  };

  const allPosts = postsData?.pages.flatMap((page) => page.posts) || [];
  const displayPosts = allPosts.map((post) => ({
    id: post._id,
    type: post.type || "",
    title: post.title || "",
    content: post.content || "",
    chapters: post.chapters || [],
    scenes: post.scenes || [],
    caption: post.caption || "",
    likes: post.stats?.likes || "0",
    comments: post.stats?.comments || "0",
    backgroundStyle: getBackgroundClass(post.styling?.background || "white"),
    fontStyle: post.styling?.font || "font-georgia",
    textColor: post.styling?.color || "black",
    imageurl: post.media?.[0]?.url || null,
    author: post.author || "",
    publishedAt: post.publishedAt,
    isLikedByCurrentUser: post.isLikedByCurrentUser || false, 
  }));

  const openModal = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    document.body.style.overflow = "unset";
  };

  const handleCreatePost = () => {
    navigate("/create-post"); // Proper React Router navigation
  };

  return (
    <>
      {/* Add custom CSS for the custom breakpoint */}
      <style>{`
        @media (min-width: 1285px) {
          .custom-xl\\:flex {
            display: flex !important;
          }
          .custom-xl\\:ml-64 {
            margin-left: 15rem !important;
          }
        }
        @media (max-width: 1284px) {
          .custom-xl\\:flex {
            display: none !important;
          }
          .custom-xl\\:ml-64 {
            margin-left: 0 !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50">
        {/* Fixed Sidebar */}
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {/* Main Content - Responsive margin */}
        <div className="custom-xl:ml-64 ml-0 min-h-screen">
          {userData && (
            <HeaderSection
              setSidebarOpen={setSidebarOpen}
              userData={userData}
              queryClient={queryClient}
              onFollowStatusChange={(isFollowing, isAccepted) => {
                const isPublic =
                  userData?.privacySettings?.isPrivateAccount === 0;
                const canView =
                  userData?.isOwnProfile ||
                  isPublic ||
                  (isFollowing && isAccepted === 1);
                setCanViewPosts(canView);
              }}
            />
          )}

          {userData?.isBlocked ? (
            <BlockedState blockedByMe={userData.blockedByMe} />
          ) : canViewPosts ? (
            <>
              <ContentTabsSection
                activeTab={activeTab}
                setActiveTab={handleTabChange}
              />

              {isLoadingPosts ? (
                <PostsGridSkeleton />
              ) : (
                <>
                  <PostsGridSection
                    filteredPosts={displayPosts}
                    openModal={openModal}
                    typeIcons={typeIcons}
                    activeTab={activeTab}
                    onCreatePost={handleCreatePost}
                  />

                  {isFetchingNextPage && (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  )}

                  <div ref={observerTarget} className="h-10" />
                </>
              )}
            </>
          ) : (
            <PrivacyLock name={userData?.name} />
          )}

          {userData?.isOwnProfile && <BottomNavbar sidebarOpen={sidebarOpen} />}
        </div>
      </div>
    </>
  );
};

export default ProfilePage;